import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, Clock, CreditCard, Users, Scan } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLANS = ['trial', 'essentiel', 'pro'];

const AdminMerchantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/merchants/${id}`, { withCredentials: true });
      setData(d);
    } catch {} finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const changePlan = async (plan) => {
    setActionLoading(`plan-${plan}`);
    try {
      await axios.post(`${API_URL}/api/admin/merchants/${id}/plan`, { plan }, { withCredentials: true });
      await fetchData();
    } catch {} finally { setActionLoading(''); }
  };

  const extendTrial = async () => {
    setActionLoading('extend');
    try {
      await axios.post(`${API_URL}/api/admin/merchants/${id}/extend-trial`, {}, { withCredentials: true });
      await fetchData();
    } catch {} finally { setActionLoading(''); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-20">Marchand introuvable</p>;

  const { merchant, cards, customers, recent_transactions, scans_total } = data;

  return (
    <div className="space-y-6" data-testid="admin-merchant-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/merchants')} data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{merchant.business_name || merchant.name}</h1>
          <p className="text-sm text-slate-500">{merchant.email}</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid sm:grid-cols-4 gap-4">
        {[
          { label: 'Cartes', value: cards.length, icon: CreditCard },
          { label: 'Clients', value: customers.length, icon: Users },
          { label: 'Scans total', value: scans_total, icon: Scan },
          { label: 'Inscription', value: merchant.created_at?.slice(0, 10), icon: Clock },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className="w-5 h-5 text-slate-400" />
              <div><p className="text-xs text-slate-500">{s.label}</p><p className="font-bold text-slate-900">{s.value}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Plan management */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle>Gestion du plan</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-slate-600">Plan actuel :</span>
            <span className="px-3 py-1 bg-slate-100 rounded-full text-sm font-medium">{merchant.subscription_plan?.toUpperCase()}</span>
            {merchant.subscription_status && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${merchant.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {merchant.subscription_status}
              </span>
            )}
            {merchant.trial_days_left !== null && merchant.trial_days_left !== undefined && (
              <span className="text-xs text-yellow-600">{merchant.trial_days_left}j trial restants</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {PLANS.map(plan => (
              <Button
                key={plan}
                variant={merchant.subscription_plan === plan ? "default" : "outline"}
                size="sm"
                onClick={() => changePlan(plan)}
                disabled={actionLoading === `plan-${plan}` || merchant.subscription_plan === plan}
                data-testid={`set-plan-${plan}`}
              >
                {actionLoading === `plan-${plan}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {plan.toUpperCase()}
              </Button>
            ))}
          </div>

          <Button variant="outline" onClick={extendTrial} disabled={actionLoading === 'extend'} data-testid="extend-trial-btn">
            {actionLoading === 'extend' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
            Prolonger trial +7 jours
          </Button>
        </CardContent>
      </Card>

      {/* Cards */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle>Cartes ({cards.length})</CardTitle></CardHeader>
        <CardContent>
          {cards.length === 0 ? <p className="text-sm text-slate-500">Aucune carte</p> : (
            <div className="space-y-2">
              {cards.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: c.primary_color || '#2563EB' }} />
                    <div>
                      <p className="font-medium text-sm text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.card_type} | {c.card_type === 'stamps' ? `${c.total_stamps} stamps` : `${c.reward_threshold} pts`}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customers */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle>Clients ({customers.length})</CardTitle></CardHeader>
        <CardContent>
          {customers.length === 0 ? <p className="text-sm text-slate-500">Aucun client</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Nom</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Code</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Visites</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.slice(0, 20).map(c => (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="py-2 px-3">{c.name}</td>
                      <td className="py-2 px-3 font-mono text-xs">{c.member_code}</td>
                      <td className="py-2 px-3 text-slate-500">{c.email}</td>
                      <td className="py-2 px-3 text-right">{c.total_visits || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customers.length > 20 && <p className="text-xs text-slate-400 mt-2 px-3">+ {customers.length - 20} autres</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent transactions */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle>Derniers scans ({recent_transactions.length})</CardTitle></CardHeader>
        <CardContent>
          {recent_transactions.length === 0 ? <p className="text-sm text-slate-500">Aucun scan</p> : (
            <div className="space-y-1">
              {recent_transactions.slice(0, 15).map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-slate-50">
                  <span className="text-slate-600">{t.customer_name || t.member_id || 'Client'}</span>
                  <span className="text-xs text-slate-400">{t.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMerchantDetailPage;
